import { Component, OnDestroy, OnInit } from '@angular/core';
import { IPersonalAddressGetData, IPersonalAddressSetData } from '@interfaces';
import { select, Store } from '@ngrx/store';
import { PageComponent } from '@shared/components/page/page.component';
import { FlowTypes, MFEContext, UserLocation } from '@shared/models';
import { ContentPageId } from '@shared/models/content-page-ids';
import {
  MfePhoneEventData,
  PhoneSetData,
  PhoneValidationStatus,
} from '@shared/models/phone-number.interface';
import { LoggerCodes } from '@shared/modules/logger/constants/logger.constants';
import { LoggerService } from '@shared/modules/logger/service/logger.service';
import { AEMService } from '@shared/services/aem/aem.service';
import { ContactInfoContent } from '@shared/services/content/content-jsons/contact-info-content';
import { getUserLocation } from '@sharedUtils';
import { UpdateMfePersonalAddressFromGetData } from '@store/actions/personal-info-address/personal-info-address.actions';
import { UpdateMfePhoneFromGetData } from '@store/actions/personal-phone/personal-phone.actions';
import * as fromRootStore from '@store/index';
import {
  getIsReviewPageReachedAndEditMode,
  getPersonalDataState,
  setIsEditingProfile,
} from '@store/index';
import { selectCurrentCollectingAccountNumber } from '@store/internal-transfers/selectors/open-account/open-account.selectors';
import { PersonalDataState } from '@store/reducers/personal-data/personal-data.reducer';
import { selectIsEditingProfile } from '@store/selectors/open-account/open-account.selectors';
import { selectMfePersonalAddressValidationStatus } from '@store/selectors/personal-info-address/personal-info-address.selectors';
import { selectMfePhone } from '@store/selectors/personal-phone/personal-phone.selectors';
import { formatPhoneNumber } from '@store/selectors/receipt/cashier-nonretirement';
import { flowTypeSelection } from '@store/selectors/triage/triage.selectors';
import { first, switchMap } from 'rxjs';

import { ContactinfoRemoteContent } from '../../../../aem/models/ContactinfoRemoteContent';
import { PageContent, toPageContent } from './contact-info-content-mapper';

@Component({
  selector: 'app-contact-info',
  templateUrl: './contact-info.component.html',
  styleUrls: ['./contact-info.component.scss'],
})
export class ContactInfoComponent
  extends PageComponent<ContactinfoRemoteContent, PageContent>
  implements OnInit, OnDestroy
{
  content = ContactInfoContent;

  personalData: PersonalDataState | undefined;
  isITMultiReceivingToggleOn$ = this.globalStore.pipe(
    select(fromRootStore.selectItMultiReceivingToggle),
  );
  accountNumber$ = this.globalStore.pipe(select(selectCurrentCollectingAccountNumber));
  isInternalTransfer: boolean = false;

  isEditing: boolean = false;
  isEditingFromReviewPage: boolean = false;
  isPhoneValid: boolean = false;

  // MFE states
  setData: Partial<IPersonalAddressSetData> = {
    runValidation: false,
    addressOnly: false,
  };
  phoneSetData: PhoneSetData = {
    phoneNumber: '',
    isMobile: false,
    runValidation: false,
  };
  private latestPhoneData = { phoneNumber: '', isMobile: false };
  ownerIndex = 0;
  isAddressValid = false;
  isContinueConfirmed = false;
  mfeContext: MFEContext = { location: UserLocation.EXTERNAL }; // safe default

  constructor(
    protected loggerService: LoggerService,
    globalStore: Store<fromRootStore.State>,
    protected aemService: AEMService,
  ) {
    super(ContentPageId.contactinfo, globalStore, {
      aemService,
      toPageContent,
    });
  }

  ngOnInit() {
    super.ngOnInit();

    // Setting up user context for MFE
    this.mfeContext = {
      location: getUserLocation(),
    };

    this.loggerService.info(
      { eventId: 'page-load', message: 'ContactInfo.Load' },
      LoggerCodes.GENERAL_SUCCESS,
    );

    this.subs.add(
      this.globalStore
        .pipe(select(fromRootStore.selectMfePersonalAddress))
        .pipe(first())
        .subscribe((personalAddress) => {
          if (personalAddress.isClientAddressesLoaded) {
            // If client addresses are already loaded, we can set the data directly
            this.setData = {
              ownerIndex: this.ownerIndex,
              personalAddress: {
                mailingAddress: personalAddress.mailingAddress,
                streetAddress: personalAddress.streetAddress,
                isStreetAddressSame: personalAddress.isStreetAddressSame,
                clientAddresses: personalAddress.clientAddresses,
                mailingAddressDropdown: personalAddress.mailingAddressDropdown,
                streetAddressDropdown: personalAddress.streetAddressDropdown,
                isClientAddressesLoaded: true,
              },
            } as Partial<IPersonalAddressSetData>;
          } else {
            // If client addresses are not loaded, we can set the data to trigger the loading
            this.setData = {
              ownerIndex: this.ownerIndex,
              personalAddress: {
                isClientAddressesLoaded: false,
              },
            } as Partial<IPersonalAddressSetData>;
          }
        }),
    );

    this.subs.add(
      this.globalStore.pipe(select(selectMfePhone), first()).subscribe((phoneState) => {
        if (phoneState?.phoneNumber) {
          this.phoneSetData = {
            phoneNumber: phoneState.phoneNumber,
            isMobile: phoneState.isMobile,
            runValidation: false,
          };
          this.latestPhoneData = {
            phoneNumber: phoneState.phoneNumber,
            isMobile: phoneState.isMobile,
          };
          this.isPhoneValid = phoneState.validationStatus === PhoneValidationStatus.VALID;
        }
      }),
    );

    this.subs.add(
      this.globalStore
        .pipe(select(selectMfePersonalAddressValidationStatus))
        .subscribe((validationStatus) => {
          this.isAddressValid = validationStatus === 'VALID';
        }),
    );

    this.subs.add(
      this.globalStore
        .pipe(select(fromRootStore.selectMfePersonalAddressIsContinueConfirmed))
        .subscribe((isContinueConfirmed) => {
          this.isContinueConfirmed = isContinueConfirmed;
        }),
    );

    this.subs.add(
      this.globalStore.select(selectIsEditingProfile).subscribe((isEditing) => {
        this.isEditing = isEditing;
      }),
    );

    this.subs.add(
      this.globalStore.select(getIsReviewPageReachedAndEditMode).subscribe((isEditing) => {
        this.isEditingFromReviewPage = isEditing;
      }),
    );

    this.globalStore.pipe(select(getPersonalDataState)).subscribe((data) => {
      this.personalData = data;
    });

    this.subs.add(
      this.globalStore.pipe(select(flowTypeSelection)).subscribe((flowType) => {
        this.isInternalTransfer = flowType === FlowTypes.INTERNAL_TRANSFER;
      }),
    );
  }

  ngAfterViewInit() {
    if (this.remoteContent$) {
      this.subs.add(
        this.remoteContent$
          .pipe(
            switchMap(() => this.globalStore.pipe(select(selectMfePhone))),
            first(),
          )
          .subscribe((phoneState) => {
            if (!phoneState?.phoneNumber && this.personalData?.phoneNumber) {
              const formattedPhone = formatPhoneNumber(this.personalData.phoneNumber);
              this.globalStore.dispatch(
                UpdateMfePhoneFromGetData({
                  payload: {
                    phoneNumber: formattedPhone,
                    isMobile: this.personalData.isMobile,
                    validationStatus: PhoneValidationStatus.VALID,
                  },
                }),
              );
              this.phoneSetData = {
                phoneNumber: formattedPhone,
                isMobile: this.personalData.isMobile,
                runValidation: false,
              };
              this.latestPhoneData = {
                phoneNumber: formattedPhone,
                isMobile: this.personalData.isMobile,
              };
              this.isPhoneValid = true;
            }
          }),
      );
    }
  }

  ngOnDestroy() {
    super.ngOnDestroy();
  }

  getData(event: CustomEvent<IPersonalAddressGetData>) {
    // Dispatching update event with every event emission
    this.globalStore.dispatch(UpdateMfePersonalAddressFromGetData({ payload: event.detail }));
  }

  getPhoneMfeData(event: MfePhoneEventData): void {
    const { phoneNumber, isMobile, validationStatus } = event.detail;
    this.isPhoneValid = validationStatus === PhoneValidationStatus.VALID;
    this.latestPhoneData = { phoneNumber, isMobile };
    this.globalStore.dispatch(
      UpdateMfePhoneFromGetData({ payload: { phoneNumber, isMobile, validationStatus } }),
    );
  }

  nextPage() {
    // Running validation for MFE Personal Address
    this.setData = { ...this.setData, runValidation: true };
    // Running validation for MFE Phone
    this.phoneSetData = { ...this.latestPhoneData, runValidation: true };

    setTimeout(() => {
      if (!this.isAddressValid || !this.isPhoneValid) {
        this.loggerService.warn(
          {
            eventId: 'contact-info-invalid-submit',
            message: 'Contact info continue blocked: address and/or phone validation failed',
            data: { addressValid: this.isAddressValid, phoneValid: this.isPhoneValid },
          },
          LoggerCodes.NULL_FAILURE,
        );
        // Don't proceed to next page if input INVALID
        return;
      } else if (this.isEditing && !this.isEditingFromReviewPage && !this.isInternalTransfer) {
        this.loggerService.info(
          {
            eventId: 'contact-info-profile-edit-narc-route',
            message:
              'Contact Info profile edit: bypassing normal flow, routing directly to new-account-review',
          },
          LoggerCodes.GENERAL_SUCCESS,
        );
        // Multi-inheritance profile edit: route directly to NARC
        this.globalStore.dispatch(setIsEditingProfile({ isEditing: false }));
        this.globalStore.dispatch(
          fromRootStore.Go({ path: ['/secure/multi-inheritance/new-account-review'] }),
        );
      } else {
        // Normal flow or ITM flow: use routing selector
        // For ITM profile edit, the selector handles routing based on isEditingProfile flag
        if (this.isContinueConfirmed) super.nextPage();
      }
    }, 1000);
  }
}
